import { useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { SideLoadedBusinessAction } from "actions";

import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import { useHasSaleyardOrDeploymentPermission } from "hooks/useHasPermission";

import { getBusinesses } from "selectors";

// Extends Record to satisfy AgGridTable's context prop, which loses excess
// property checking.
export interface SideLoadedBusinessGridContext extends Record<string, unknown> {
  businessesById: StateModelById<Business>;
  canIgnore: boolean;
  handleSelectAndCompare: (recordId: string) => void;
  handleSetIgnored: (recordId: string, isIgnored: boolean) => void;
}

export const getSideLoadedBusinessRowId = (params: {
  data: SideLoadedBusiness;
}): string => params.data.id;

export function useSideLoadedBusinessGridContext(
  handleSelectAndCompare: (recordId: string) => void,
): SideLoadedBusinessGridContext {
  const businessesById: StateModelById<Business> = useSelector(getBusinesses);
  const dispatch = useDispatch();

  // Records don't carry permissions - curating them is gated at the data set
  // level, which for us is the management feature permission.
  const canIgnore = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureSideLoadedDataManagement,
    SaleyardPermission.featureSideLoadedDataManagement,
  );

  const handleSetIgnored = useCallback(
    (recordId: string, isIgnored: boolean) => {
      dispatch(SideLoadedBusinessAction.update({ id: recordId, isIgnored }));
    },
    [dispatch],
  );

  return useMemo(
    () => ({
      businessesById,
      canIgnore,
      handleSelectAndCompare,
      handleSetIgnored,
    }),
    [businessesById, canIgnore, handleSelectAndCompare, handleSetIgnored],
  );
}
